import { Box, Button, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { cloneDeep, isEqual, some, uniqBy } from 'lodash';
import { UpdateProductTabProps } from '../../../../interfaces/Product/UpdateProductTab';
import { sharedStyles } from '../../../../utils/Style';
import { OptionKeyCard } from './OptionKeyCard';
import {
  LocalizedProductOptionValue,
  ProductOptionKey,
} from '../../../../interfaces/ProductOption/ProductOptionKey';
import { UpsertOptionKeyDialog } from './UpsertOptionKeyDialog';
import { UpsertProductOptionDialog } from './UpsertProductOptionDialog';
import {
  ProductOption,
  productOptionFromProduct,
} from '../../../../interfaces/ProductOption/ProductOption';
import { ProductOptionCard } from './ProductOptionCard';
import {
  getEmptyProduct,
  Product,
} from '../../../../interfaces/Product/Product';
import { getProduct } from '../../../../api/Product';
import { Waiting } from '../../../../components/Waiting/Waiting';
import { ProductOptionKVP } from '../../../../interfaces/ProductOption/ProductOptionKVP';
import {
  getProductOptions,
  updateProductOptions,
} from '../../../../api/ProductOption';
import {
  buildProductOptionKeys,
  getOptionUniqueIdentifier,
} from '../../../../utils/ProductOptions';
import { LanguageDropdown } from '../../../../components/Dropdowns/LanguageDropdown';

export const UpdateProductOptions = (props: UpdateProductTabProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [keys, setKeys] = useState<ProductOptionKey[]>([]);
  const [options, setOptions] = useState<ProductOption[]>([]);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [showInsertKeyDialog, setShowInsertKeyDialog] = useState(false);
  const [showAddOptionDialog, setShowAddOptionDialog] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null);

  useEffect(() => {
    setLoadingProduct(true);
    getProduct(props.productID)
      .then((p) => setProduct(p))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingProduct(false));

    setLoadingOptions(true);
    getProductOptions(props.productID)
      .then((res) => {
        setKeys(buildProductOptionKeys(res));
        setOptions(res.options);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOptions(false));
  }, []);

  const handleAddKey = (newKey: string) => {
    if (keys.map((key) => key.key).includes(newKey)) {
      toast.error('Bu seçenek zaten mevcut');
      return;
    }

    const defaultValue: LocalizedProductOptionValue = {
      name: `${newKey}-1`,
      nameTranslation: {},
    };
    const newKeys: ProductOptionKey[] = [
      ...keys,
      { key: newKey, nameTranslation: {}, values: [defaultValue] },
    ];

    setKeys(newKeys);
    setOptions(
      options.map((option) => ({
        ...option,
        kvps: [
          ...option.kvps,
          { key: newKey, value: defaultValue.name, valueNameTranslation: {} },
        ],
      })),
    );

    setShowInsertKeyDialog(false);
  };

  const handleDeleteKey = (index: number) => {
    setKeys([...keys.slice(0, index), ...keys.slice(index + 1)]);

    const newOptions: ProductOption[] =
      keys.length === 1
        ? []
        : options.map((option) => ({
            ...option,
            kvps: option.kvps.toSpliced(index, 1),
          }));

    setOptions(uniqBy(newOptions, (x) => getOptionUniqueIdentifier(x)));
  };

  const handleUpdateKeyName = (index: number, newName: string) => {
    if (selectedLocale) {
      handleUpdateLocalizedKeyName(index, newName);
      return;
    }

    if (keys.map((key) => key.key).includes(newName)) {
      toast.error('Bu seçenek zaten mevcut');
      return;
    }

    const newKeys = cloneDeep(keys);
    newKeys[index].key = newName;

    const newOptions: ProductOption[] = options.map((option) => {
      const newKVPs = cloneDeep(option.kvps);
      newKVPs[index].key = newName;

      return { ...option, kvps: newKVPs };
    });
    setOptions(newOptions);

    setKeys(newKeys);
  };

  const handleUpdateLocalizedKeyName = (index: number, newName: string) => {
    if (!selectedLocale) return;

    if (
      keys
        .map((key) => key.nameTranslation[selectedLocale] ?? '')
        .includes(newName)
    ) {
      toast.error('Bu seçenek zaten mevcut');
      return;
    }

    const newKeys = cloneDeep(keys);
    newKeys[index].nameTranslation[selectedLocale] = newName;

    setKeys(newKeys);
  };

  const handleAddValue = (keyIndex: number, newValue: string) => {
    if (keys[keyIndex].values.map((value) => value.name).includes(newValue)) {
      toast.error('Bu değer zaten mevcut');
      return;
    }

    const newKeys = cloneDeep(keys);
    newKeys[keyIndex].values.push({ name: newValue, nameTranslation: {} });

    setKeys(newKeys);
  };

  const handleDeleteValue = (keyIndex: number, valueIndex: number) => {
    const deletedValue = keys[keyIndex].values[valueIndex];

    const newKeys = cloneDeep(keys);
    newKeys[keyIndex].values.splice(valueIndex, 1);

    setKeys(newKeys);
    setOptions(
      options.filter(
        (option) => option.kvps[keyIndex].value !== deletedValue.name,
      ),
    );
  };

  const handleUpdateValue = (
    keyIndex: number,
    valueIndex: number,
    newValue: string,
  ) => {
    const existingValue = keys[keyIndex].values[valueIndex];

    if (
      !selectedLocale &&
      keys[keyIndex].values.map((value) => value.name).includes(newValue)
    ) {
      toast.error('Bu değer zaten mevcut');
      return;
    }

    const newKeys = cloneDeep(keys);
    newKeys[keyIndex].values[valueIndex] = {
      name: selectedLocale ? keys[keyIndex].values[valueIndex].name : newValue,
      nameTranslation: selectedLocale
        ? {
            ...keys[keyIndex].values[valueIndex].nameTranslation,
            [selectedLocale]: newValue,
          }
        : {},
    };

    setKeys(newKeys);
    setOptions(
      options.map((option) => {
        if (option.kvps[keyIndex].value !== existingValue.name) {
          return option;
        }

        const newKVPs = cloneDeep(option.kvps);
        if (selectedLocale) {
          newKVPs[keyIndex].valueNameTranslation[selectedLocale] = newValue;
        } else {
          newKVPs[keyIndex].value = newValue;
        }

        return { ...option, kvps: newKVPs };
      }),
    );
  };

  const handleAddOption = (values: string[]) => {
    if (
      some(options, (x) =>
        isEqual(
          values,
          x.kvps.map((kvp) => kvp.value),
        ),
      )
    ) {
      toast.error('Bu kombinasyonlarla eşleşen bir varyant zaten mevcut');
      return;
    }

    const kvps: ProductOptionKVP[] = keys.map((key, i) => ({
      key: key.key,
      value: values[i],
      valueNameTranslation: {},
    }));

    const option = productOptionFromProduct(
      product ?? getEmptyProduct(),
      options.length,
      kvps,
    );
    setOptions([...options, option]);
    setShowAddOptionDialog(false);
  };

  const handleDeleteOption = (index: number) => {
    const newOptions = cloneDeep(options);
    newOptions.splice(index, 1);

    setOptions(newOptions);
  };

  const handleUpdateOption = (index: number, newValue: ProductOption) => {
    const newOptions = [...options];
    newOptions[index] = newValue;

    setOptions(newOptions);
  };

  const handleUpdateOptionValues = (index: number, newValues: string[]) => {
    if (
      some(options, (x) =>
        isEqual(
          newValues,
          x.kvps.map((kvp) => kvp.value),
        ),
      )
    ) {
      toast.error('Bu kombinasyonlarla eşleşen bir varyant zaten mevcut');
      return;
    }

    const newOptions = [...options];
    newOptions[index].kvps = options[index].kvps.map((kvp, i) => ({
      ...kvp,
      value: newValues[i],
    }));

    setOptions(newOptions);
  };

  const handleSave = () => {
    setLoadingOptions(true);
    updateProductOptions(props.productID, keys, options)
      .then((res) => {
        toast.success('Varyantlar başarıyla kaydedildi');
        setKeys(buildProductOptionKeys(res));
        setOptions(res.options);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOptions(false));
  };

  const goBack = () => navigate(`/products/catalog${location.search}`);

  const loading = loadingOptions || loadingProduct;

  return (
    <Box sx={{ ...sharedStyles.pageBody, p: 3, gap: 1.5 }}>
      <Waiting open={loading} />
      <UpsertOptionKeyDialog
        open={showInsertKeyDialog}
        selectedLocale={null}
        onUpsert={handleAddKey}
        onClose={() => setShowInsertKeyDialog(false)}
      />
      <UpsertProductOptionDialog
        keys={keys}
        open={showAddOptionDialog}
        onUpsert={handleAddOption}
        onClose={() => setShowAddOptionDialog(false)}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Ürün Varyantları</Typography>
      </Box>

      {keys.length > 0 && (
        <Box sx={{ width: 150 }}>
          <LanguageDropdown
            label='Dil'
            allowDefault
            value={selectedLocale}
            onChange={setSelectedLocale}
          />
        </Box>
      )}

      {keys.map((key, i) => (
        <OptionKeyCard
          key={key.key}
          optionKey={key}
          selectedLocale={selectedLocale}
          onDelete={() => handleDeleteKey(i)}
          onUpdateName={(newName) => handleUpdateKeyName(i, newName)}
          onAddValue={(value) => handleAddValue(i, value)}
          onDeleteValue={(valueIndex) => handleDeleteValue(i, valueIndex)}
          onUpdateValue={(valueIndex, newValue) =>
            handleUpdateValue(i, valueIndex, newValue)
          }
        />
      ))}

      {!selectedLocale && (
        <>
          <Button
            color='primary'
            onClick={() => setShowInsertKeyDialog(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            + Yeni Seçenek
          </Button>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            {options.map((option, i) => (
              <ProductOptionCard
                key={i}
                value={option}
                keys={keys}
                onChange={(newValue) => handleUpdateOption(i, newValue)}
                onDelete={() => handleDeleteOption(i)}
                onUpdateValues={(newValues) =>
                  handleUpdateOptionValues(i, newValues)
                }
              />
            ))}
            {keys.length > 0 && (
              <Button
                color='primary'
                onClick={() => setShowAddOptionDialog(true)}
                sx={{ ...sharedStyles.buttonText }}
              >
                + Yeni Varyant
              </Button>
            )}
          </Box>
        </>
      )}

      <Button
        variant='contained'
        onClick={handleSave}
        sx={{ ...sharedStyles.buttonText }}
      >
        Kaydet
      </Button>
    </Box>
  );
};
